/* Cookie consent banner — first-visit only, dismissable, persists choice in localStorage.
   Implicit on accept (or after dismiss) — Google Analytics tracking only fires when the
   user hasn't explicitly rejected. Reject = we disable gtag entirely and use the bare
   minimum strictly-necessary cookies (the JWT session cookie / our own auth localStorage). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";

const KEY = "rf_cookie_consent"; // "accepted" | "rejected"

export function applyConsentSideEffects(choice) {
  // Google Analytics — pause/resume tracking
  try {
    if (typeof window.gtag === "function") {
      const v = choice === "rejected" ? "denied" : "granted";
      window.gtag("consent", "update", { analytics_storage: v, ad_storage: v });
    }
  } catch {}

  // Microsoft Clarity — lazy-load the tag only after the user accepts.
  // We can't cleanly UNLOAD Clarity if they later change their mind (no supported
  // API), but we do guard against double-injection with the `__rfClarityLoaded` flag.
  try {
    if (choice === "accepted" && !window.__rfClarityLoaded) {
      window.__rfClarityLoaded = true;
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i + "?ref=bwt";
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", "y8iw7xkrlz");
    }
  } catch {}
}

export default function CookieBanner() {
  const [choice, setChoice] = useState(() => {
    try { return localStorage.getItem(KEY); } catch { return null; }
  });

  // Wire any existing tracker to the persisted choice on every mount (covers SPA navigation)
  useEffect(() => {
    if (choice) applyConsentSideEffects(choice);
  }, [choice]);

  const persist = (c) => {
    try { localStorage.setItem(KEY, c); } catch {}
    applyConsentSideEffects(c);
    setChoice(c);
  };

  if (choice) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-3 left-3 right-3 sm:bottom-4 sm:left-6 sm:right-6 sm:max-w-3xl sm:mx-auto z-[70] animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="bg-rf-ink text-rf-cream rounded-2xl shadow-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 border border-rf-amber/40">
        <div className="hidden sm:flex flex-shrink-0 w-11 h-11 rounded-xl bg-rf-amber/25 items-center justify-center text-rf-amber">
          <Cookie size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-extrabold text-sm sm:text-base leading-tight">We use a few biscuits to keep things running</p>
          <p className="sm:hidden text-xs text-rf-cream/75 mt-1 leading-snug">
            Sign-in cookies plus light analytics. Using the site means you agree to our{" "}
            <Link to="/terms" className="underline font-bold text-rf-amber">Terms</Link> and{" "}
            <Link to="/privacy" className="underline font-bold text-rf-amber">Privacy Policy</Link>.
          </p>
          <p className="hidden sm:block text-sm text-rf-cream/75 mt-1 leading-snug">
            Recipe Filer uses strictly-necessary cookies for sign-in and a small amount of analytics so we know what's helpful.
            By <b>using this site you agree to our</b>{" "}
            <Link to="/terms" className="underline font-bold text-rf-amber hover:text-rf-amber/80">Terms &amp; Conditions</Link> and{" "}
            <Link to="/privacy" className="underline font-bold text-rf-amber hover:text-rf-amber/80" data-testid="cookie-privacy-link">Privacy Policy</Link>.
            You can opt out of analytics below — the site still works perfectly.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0 [&>button]:flex-1 sm:[&>button]:flex-none">
          <button
            data-testid="cookie-reject"
            onClick={() => persist("rejected")}
            className="px-4 py-2.5 sm:py-2 rounded-full bg-rf-cream/10 hover:bg-rf-cream/20 text-rf-cream text-sm font-bold transition-colors"
          >
            Reject analytics
          </button>
          <button
            data-testid="cookie-accept"
            onClick={() => persist("accepted")}
            className="px-4 py-2.5 sm:py-2 rounded-full bg-rf-amber hover:bg-rf-amber/90 text-rf-ink text-sm font-bold transition-colors"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
